'use client';

import { CustomError } from '@core/utils';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import type { Comment } from '@/api/getCommentList';
import { ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useAuthStore } from '@/stores/AuthStore';

import {
  scrollToCommentTarget,
  useCommentPostSuccess,
  useCommentThreadHashScroll,
} from './_hooks/useCommentThreadEffects';
import { useCommentThreadNavigation } from './_hooks/useCommentThreadNavigation';
import {
  CommentField,
  type CommentFieldProps,
  type CommentFieldSubmitResult,
} from './CommentField';
import type { CommentListProps } from './CommentList';
import { CommentList } from './CommentList';

type CommentFieldOptions = Pick<
  CommentFieldProps,
  'className' | 'sticky' | 'topDivider' | 'topSlot' | 'withPortal'
>;

/**
 * 답글/수정/idle 세 상태는 항상 배타라 단일 union으로 묶는다.
 * 두 useState로 나눠두면 setter 한쪽을 비우는 명령형 동기화가 매번 필요해 실수 가능성이 생긴다.
 */
type ComposerMode =
  | { kind: 'idle' }
  | {
      kind: 'reply';
      rootCommentId: number;
      parentCommentId: number;
      replyTo: string;
    }
  | { kind: 'edit'; commentId: number; contents: string };

const IDLE_COMPOSER: ComposerMode = { kind: 'idle' };

export interface CommentThreadProps extends Pick<
  CommentListProps,
  | 'commentType'
  | 'contentId'
  | 'initialData'
  | 'initialPageData'
  | 'rootCommentId'
  | 'className'
> {
  /** 글 제목 */
  title?: string;
  /** 정책 이름 */
  policyName?: string;
  /** 첫 렌더 시 입력창에 포커스를 줄지 여부다. */
  initOnFocus?: boolean;
  /** 리스트만 쓰는 화면을 위해 입력창 렌더 여부를 분리한다. */
  hasCommentField?: boolean;
  /** 화면별 sticky/slot 차이를 주입하기 위한 CommentField 옵션 묶음이다. */
  commentFieldProps?: CommentFieldOptions;
}

export const CommentThread = ({
  initOnFocus,
  commentType,
  contentId,
  title,
  policyName,
  initialData,
  initialPageData,
  rootCommentId,
  className,
  hasCommentField = true,
  commentFieldProps,
}: CommentThreadProps) => {
  const pathname = usePathname();
  const isLogin = useAuthStore((state) => state.isLogin);
  const router = useCustomRouter();
  const [commentData, setCommentData] = useState(initialData);
  const [composerMode, setComposerMode] = useState<ComposerMode>(IDLE_COMPOSER);
  const [focusTrigger, setFocusTrigger] = useState(0);

  const { sortType, handleChangeSortType, resetSortType } =
    useCommentThreadNavigation();

  // URL hash로 댓글 영역에 진입한 경우 리스트 상단 위치를 재보정한다.
  useCommentThreadHashScroll(pathname ?? '');

  const handleCommentPostSuccess = useCommentPostSuccess({
    commentType,
    contentId,
    title,
    policyName,
    sortType,
    resetSortType,
  });

  const openComposer = () => {
    // 같은 reply/edit 모드를 다시 눌러도 focus effect가 한 번 더 돌도록 trigger를 올린다.
    setFocusTrigger((prev) => prev + 1);
  };

  const clearComposerMode = () => setComposerMode(IDLE_COMPOSER);

  const requireLogin = () => {
    if (isLogin) return false;

    const url = new URL(window.location.href);
    router.push(
      ROUTES.LOGIN.withSearchParams({
        searchParams: { redirect: `${url.pathname}${url.search}` },
      }),
    );

    return true;
  };

  const handleClickReplyButton = (
    comment: Comment,
    nextRootCommentId: number,
    nextParentCommentId: number,
  ) => {
    if (requireLogin()) return;

    setComposerMode({
      kind: 'reply',
      rootCommentId: nextRootCommentId,
      parentCommentId: nextParentCommentId,
      replyTo: comment.nickname,
    });
    openComposer();
  };

  const handleClickEditButton = (comment: Comment) => {
    if (requireLogin()) return;

    // 수정은 별도 페이지 대신 하단 composer에 현재 댓글 내용을 그대로 주입한다.
    setComposerMode({
      kind: 'edit',
      commentId: comment.commentId,
      contents: comment.contents,
    });
    openComposer();
  };

  const clearEditModeByDeletedComment = (deletedCommentId: number) => {
    if (
      composerMode.kind === 'edit' &&
      composerMode.commentId === deletedCommentId
    ) {
      clearComposerMode();
    }
  };

  // 답글의 부모 삭제 등으로 수정 대상 댓글이 placeholder화되는 경우에도 composer를 reset한다.
  useEffect(() => {
    if (composerMode.kind !== 'edit' || !commentData) return;

    const findComment = (comments: Comment[]): Comment | undefined => {
      for (const c of comments) {
        if (c.commentId === composerMode.commentId) return c;
        if (c.childCommentList) {
          const found = findComment(c.childCommentList);
          if (found) return found;
        }
      }
    };

    const target = findComment(commentData.commentList);
    const isHidden =
      !target ||
      target.isBlocked ||
      target.isDeleted ||
      target.isReport ||
      !target.isDisplay;

    if (isHidden) clearComposerMode();
  }, [commentData, composerMode]);

  const contextualSlot =
    composerMode.kind === 'idle' ? null : (
      <section className="flex items-center justify-between gap-12 rounded-small bg-Gray-50 px-12 py-8">
        <div className="min-w-0 flex-1">
          <strong className="block truncate font-600 text-Gray-900 typo-caption1">
            {composerMode.kind === 'edit' ? '댓글 수정 중' : '답글 작성 중'}
          </strong>
          <p className="truncate text-Gray-700 typo-caption1">
            {composerMode.kind === 'edit'
              ? '수정 후 전송 버튼을 누르면 댓글이 바로 반영돼요.'
              : `@${composerMode.replyTo}님에게 답글을 남기고 있어요.`}
          </p>
        </div>
        <button
          className="shrink-0 font-500 text-Gray-700 typo-caption1"
          type="button"
          onClick={clearComposerMode}
        >
          취소
        </button>
      </section>
    );

  // 기존 정책 액션 슬롯과 답글/수정 상태 안내를 같은 sticky 영역 안에서 합친다.
  const composerTopSlot =
    !commentFieldProps?.topSlot && !contextualSlot ? undefined : (
      <div className="flex flex-col gap-12">
        {commentFieldProps?.topSlot}
        {contextualSlot}
      </div>
    );

  const shouldRenderCommentField =
    hasCommentField && (composerTopSlot !== undefined || isLogin);

  const handleFieldSuccess = (result: CommentFieldSubmitResult) => {
    // 수정은 목록 데이터 갱신 후 같은 위치로 다시 스크롤만 맞추면 된다.
    if (result.type === 'edit') {
      clearComposerMode();
      scrollToCommentTarget(result.commentId);

      return;
    }

    if (!result.response) throw new CustomError();

    const point = result.response.pointActivity?.resultYN
      ? result.response.pointActivity.addPoints
      : undefined;

    // 작성/답글은 서버 응답의 신규 댓글 id 기준으로 후속 스크롤과 토스트를 처리한다.
    clearComposerMode();
    handleCommentPostSuccess(result.response.welloCommentId, point);
  };

  return (
    <>
      <CommentList
        className={className}
        commentType={commentType}
        contentId={contentId}
        initialData={commentData}
        initialPageData={initialPageData}
        rootCommentId={rootCommentId}
        sortType={sortType}
        onDeleteSuccess={clearEditModeByDeletedComment}
        onChangeCommentData={setCommentData}
        onChangeSortType={handleChangeSortType}
        onClickEditButton={handleClickEditButton}
        onClickReplyButton={handleClickReplyButton}
      />
      {shouldRenderCommentField ? (
        <CommentField
          commentType={commentType}
          contentId={contentId}
          initOnFocus={initOnFocus}
          parentCommentId={
            composerMode.kind === 'reply'
              ? composerMode.parentCommentId
              : undefined
          }
          replyTo={
            composerMode.kind === 'reply' ? composerMode.replyTo : undefined
          }
          rootCommentId={
            composerMode.kind === 'reply'
              ? composerMode.rootCommentId
              : rootCommentId
          }
          editingCommentId={
            composerMode.kind === 'edit' ? composerMode.commentId : undefined
          }
          editingContents={
            composerMode.kind === 'edit' ? composerMode.contents : undefined
          }
          focusTrigger={focusTrigger || undefined}
          {...commentFieldProps}
          topSlot={composerTopSlot}
          onSuccess={handleFieldSuccess}
        />
      ) : null}
    </>
  );
};
